import {
	Component,
	ElementRef,
	EventEmitter,
	Output,
	ViewChild,
} from '@angular/core';
import { DeviceDataRequest } from 'src/app/models/data/device';
import { ApiLoadingKeys, ApiService } from 'src/app/services/api.services';
import {
	AddDeviceGroupRequestStatus,
	AddDeviceGroupStatusEvent,
	EditDeviceGroup,
} from 'src/app/ui/add-device-group/add-device-group-main-tab.component';
import { ActivatedRoute, Router } from '@angular/router';
import { MatStepper } from '@angular/material/stepper';
import { NavigationHistoryService } from 'src/app/services/navigation-history.service';
import { AddDeviceGroupDevicesComponent } from 'src/app/ui/add-device-group/add-device-group-devices/add-device-group-devices.component';
import { MatDialog } from '@angular/material/dialog';
import { CancelConfirmationComponent } from 'src/app/components/cancel-confirmation/cancel-confirmation.component';
import { BehaviorSubject } from 'rxjs';
import { AppNavigationHeaderComponent } from '../../../../components/navigation-header/app-navigation-header.component';
import { AccessHorizontalStepperLabelsComponent } from '../../../../access-horizontal-stepper/access-horizontal-stepper-labels.component';
import { AddDeviceGroupConfirmationComponent } from '../../../add-device-group/add-device-group-confirmation/add-device-group-confirmation.component';
import { AddDeviceGroupDevicesComponent as AddDeviceGroupDevicesComponent_1 } from '../../../add-device-group/add-device-group-devices/add-device-group-devices.component';

enum StepperPage {
	addDevices = 0,
	confirmation = 1,
}

@Component({
	selector: 'app-add-devices-to-group',
	templateUrl: './add-devices-to-group.component.html',
	styleUrls: [
		'./add-devices-to-group.component.scss',
		'../../../common/stepper.scss',
	],
	imports: [
		AppNavigationHeaderComponent,
		AccessHorizontalStepperLabelsComponent,
		AddDeviceGroupConfirmationComponent,
		AddDeviceGroupDevicesComponent_1,
	],
})
export class AddDevicesToGroupComponent {
	addDeviceGroupRequestStatusEvents =
		new BehaviorSubject<AddDeviceGroupStatusEvent>({
			status: AddDeviceGroupRequestStatus.loading,
		});

	deviceGroupId: string;
	deviceGroupName = '';
	count = 0;
	isEditable = false;
	@ViewChild('stepperRef') stepperRef!: ElementRef;
	@ViewChild(AddDeviceGroupDevicesComponent)
	addDeviceGroupDevice?: AddDeviceGroupDevicesComponent;

	stepper!: MatStepper;
	editDeviceGroup: EditDeviceGroup = { id: '', name: '', deviceIds: [] };

	// Access horizontal stepper properties
	steps: string[] = [
		$localize`:@@addDeviceGroupName-addDevices:Add Devices`,
		$localize`:@@addDeviceGroupName-confirmation:Confirmation`,
	];
	currentStep: number = 0;

	constructor(
		private readonly apiService: ApiService,
		private readonly route: ActivatedRoute,
		private readonly router: Router,
		private readonly navHistory: NavigationHistoryService,
		private dialog: MatDialog
	) {
		this.deviceGroupId = route.snapshot.params.id;
		this.deviceGroupName =
			router.lastSuccessfulNavigation?.extras?.state?.deviceGroupName;
	}

	async addDevicesToGroup(devicesIds: string[]): Promise<boolean> {
		this.count = devicesIds.length;
		const deviceGroupDeviceCount =
			await this.apiService.updateDeviceGroupMembership(
				this.deviceGroupId,
				devicesIds,
				'add'
			);
		if (deviceGroupDeviceCount.overLimit > 0) {
			const deviceOverLimit = deviceGroupDeviceCount.overLimit;
			this.dialog.open(CancelConfirmationComponent, {
				panelClass: 'styled-dialog-container',
				maxWidth: '610px',
				minHeight: '268px',
				data: {
					dialogHeader: $localize`:@@deviceGroupAddDeviceLimit-header:Device Limit Exceeded`,
					dialogContent: $localize`:@@deviceGroupAddDeviceLimit-content:You have exceeded the device group limit. Remove ${deviceOverLimit} ${deviceOverLimit === 1 ? 'device' : 'devices'} to continue.`,
					dialogPrimaryButtonText: $localize`:@@deviceGroupAddDeviceLimit-button:Manage Devices`,
					showDevider: true,
				},
			});
			return false;
		}
		this.addDeviceGroupRequestStatusEvents.next({
			status: AddDeviceGroupRequestStatus.success,
			data: { created: false },
		});
		return true;
	}

	readonly availableDevicesLoadingKey = ApiLoadingKeys.getAvailableDevices;

	readonly getAvailableDevices: DeviceDataRequest = (filters) =>
		this.apiService.getAvailableDevices(this.deviceGroupId, filters);

	isHidden = (): boolean => {
		switch (this.currentStep) {
			case StepperPage.confirmation:
				return false;
			default:
				return true;
		}
	};

	isValid = (): boolean => {
		switch (this.currentStep) {
			case StepperPage.addDevices:
				return this.addDeviceGroupDevice?.isValid ?? false;
		}
		return false;
	};

	movePrevious(): void {
		if (this.currentStep === StepperPage.addDevices) {
			this.navHistory.navigateBack();
		} else {
			this.currentStep--;
		}
	}

	async moveNext(): Promise<void> {
		switch (this.currentStep) {
			case StepperPage.addDevices:
				const devicesAddedSuccessfully = await this.addDevicesToGroup(
					this.editDeviceGroup.deviceIds
				);
				if (!devicesAddedSuccessfully) {
					return;
				}
				this.currentStep++;
				break;
			case StepperPage.confirmation:
				break;
		}
	}
}
